<template>
  <div class="line-items-view">
    <div class="line-items-view__container" style="margin-top: 53px">
      <div class="flex-container">
        <div class="line-items-view__action select">
          <span>Filter By Plant</span>
          <MultiSelect
            class="line-items-view__select"
            :items="plants"
            v-model:value="selectedPlants"
            :auto-close="false"
            placeholder="Select Plants"
            itemsName="Plants"
            data-cy="plant-select"
          />
        </div>
        <div class="line-items-view__action dropdown">
          <span>Sort By</span>
          <DropDownList
            class="line-items-view__sort-dropdown"
            :data-items="sortOptions"
            :text-field="'text'"
            v-model="selectedSortOption"
            placeholder="Sort By"
            data-cy="sort-dropdown"
          />
        </div>
      </div>
    </div>
    <div class="line-items-view__header">
      <div class="line-items-view__switch-mode">
        <input
          type="checkbox"
          id="line-item-view-mode"
          v-model="useOriginalView"
        />
        <label for="line-item-view-mode">{{
          useOriginalView
            ? "Switch to Current Product View"
            : "Switch to Original Submissions View"
        }}</label>

        <div class="line-items-view__switch-mode-items">
          {{ lineItems.length ? lineItems.length : "-" }} item(s) in submission.
        </div>
      </div>
      <div>
        <span v-if="isPesachRequest && isDairyRequest">
          ***Includes Pesach and Dairy Equipment request(s)</span
        >
        <span v-else-if="isPesachRequest"> ***Includes Pesach request(s)</span>
        <span v-else-if="isDairyRequest">
          ***Includes Dairy Equipment request(s)</span
        >
      </div>
    </div>
    <div
      v-if="submission?.Submission.JobComment"
      class="line-items-view__container yellow"
    >
      <header>Customer General Comment</header>
      <div>
        {{ submission?.Submission.JobComment }}
      </div>
    </div>
    <div
      v-if="autoCertifiedCount > 0"
      class="line-items-view__container yellow"
    >
      <span>{{ autoCertifiedCount }} product(s) CERTIFIED automatically.</span>
    </div>
    <div class="line-items-view__items">
      <LineItemCard
        v-for="lineItem in filteredLineItems"
        :key="lineItem.Number!"
        :lineItem="lineItem"
        :originalView="useOriginalView"
      />
      <Loader
        v-if="loadingLineItems"
        class="line-items-view__loader"
        size="large"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import LineItemCard from "./LineItemCard.vue";
import { Loader } from "@progress/kendo-vue-indicators";
import type { WfProductSubmissionDisplay } from "@/Domain/WfProductSubmissionDisplay";
import { EnumDpm } from "@/Domain/EnumDpm";
import type { IdAndName } from "@/Domain/IdAndName";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import type { ProductSubmissionLineItemDisplay } from "@/Domain/ProductSubmissionLineItemDisplay";

const main = useMainStore();

const props = defineProps<{
  workflowId: number;
  submission?: WfProductSubmissionDisplay;
  attachments: SharePointAttachment[];
}>();

const lineItems = ref<ProductSubmissionLineItemDisplay[]>([]);
const selectedPlants = ref<IdAndName[]>([]);
const useOriginalView = ref<boolean>(false);

const loadingLineItems = ref<boolean>(false);

const sortOptions = [
  { text: "Line Item", value: "ById" },
  { text: "Label Name", value: "ByName" },
  { text: "Plant", value: "ByPlant" },
];

const selectedSortOption = ref(sortOptions[0]);

const autoCertifiedCount = computed(() => {
  return lineItems.value.filter(
    (li) => li.ProductValidationComments === "Auto-Certified"
  ).length;
});

const isPesachRequest = computed(() => {
  return lineItems.value.some((li) => li.RequestedPesach);
});

const isDairyRequest = computed(() => {
  return lineItems.value.some(
    (li) => li.RequestedDpm == EnumDpm.DairyEquipment
  );
});

const plants = computed(() => {
  return lineItems.value
    .map(
      (li) =>
        ({
          ID: li.OwnsId,
          Name: li.OwnsName,
          Status: li.OwnsStatus,
        } as IdAndName)
    )
    .filter(
      (value, index, self) => self.findIndex((p) => p.ID == value.ID) === index
    );
});

const filteredLineItems = computed(() => {
  let items = [...lineItems.value];

  if (selectedPlants.value.length > 0) {
    items = items.filter((li) =>
      selectedPlants.value.some((p) => p.ID == li.OwnsId)
    );
  }

  if (selectedSortOption.value.value == "ById") {
    items = items.sort((a, b) => (a.Number ?? 0) - (b.Number ?? 0));
  } else if (selectedSortOption.value.value == "ByName") {
    items = items.sort(
      (a, b) =>
        a.LabelNameNum.localeCompare(b.LabelNameNum) ||
        a.OwnsName.localeCompare(b.OwnsName)
    );
  } else if (selectedSortOption.value.value == "ByPlant") {
    items = items.sort((a, b) => a.OwnsName.localeCompare(b.OwnsName));
  }

  return items;
});

const getProductSubmissionLineItemsAsync = async function () {
  loadingLineItems.value = true;

  await handleResponsePromiseAsync(
    api.get("GetWfProductSubmissionLineItems", {
      id: props.workflowId,
    }),
    (data) => {
      lineItems.value = data;

      // Set HasAttachments property for each line item based on the attachments
      setHasAttachments();
    },
    "LineItemsView.vue",
    "getProductSubmissionLineItemsAsync"
  );

  selectedPlants.value = [...plants.value];

  loadingLineItems.value = false;
};

const setHasAttachments = function () {
  lineItems.value.forEach((li) => {
    li.HasAttachments = props.attachments.some(
      (a) => a.CartId == li.ProductInCartID
    );
  });
};

watchEffect(() => {
  setHasAttachments();
});

onBeforeMount(async () => {
  await getProductSubmissionLineItemsAsync();
});
</script>

<style scoped lang="scss">
.flex-container {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}
.line-items-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: "museo-sans", helvetica, arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-size-adjust: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    gap: 16px;
  }

  &__switch-mode {
    margin-left: -4px;
    font-size: 11px;
    color: #999;

    &-items {
      margin-left: 6px;
    }
  }

  &__loader {
    margin: 0 auto;
    width: fit-content;
    display: block;
  }

  &__container {
    border: 1px solid #ddd;
    padding: 8px 15px;
    background: #dedede;
    > header {
      font-weight: 400;
      margin: -10px -15px 10px -15px;
      padding: 8px 15px;
      font-size: 13px;
      background: #dedede;
      border-bottom: 1px solid #e8e8e8;
      display: flex;
      justify-content: space-between;
    }
    > footer {
      padding: 8px 15px;
      font-size: 13px;
      background: #efefef;
      border-top: 1px solid #e8e8e8;
      margin: 10px -15px -8px;
    }

    &.yellow {
      background: #fde9b3;
      color: #666;
      > header {
        background: #f3dfaa;
        border-bottom: 1px solid #e7d4a0;
      }
    }
  }

  &__action {
    font-weight: normal;

    &.select {
      width: 70%;
    }

    &.dropdown {
      width: 30%;
    }
  }

  .k-input-inner {
    font-size: 12px;
  }
}

.k-grid-header {
  background-color: #dedede;
}
</style>
